import * as React from 'react';
import { useFormContext, Controller, type FieldValues, type Path } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface FormFieldBaseProps<T extends FieldValues> {
  name: Path<T>;
  label?: string;
  hint?: string;
  required?: boolean;
  className?: string;
}

export interface FormFieldProps<T extends FieldValues> extends FormFieldBaseProps<T> {
  children: (fieldProps: {
    value: unknown;
    onChange: (value: unknown) => void;
    onBlur: () => void;
    id: string;
    invalid: boolean;
  }) => React.ReactNode;
}

/**
 * Wrapper base que provee label + error + hint.
 * Los XField concretos se construyen sobre este.
 */
export function FormField<T extends FieldValues>({
  name,
  label,
  hint,
  required,
  className,
  children,
}: FormFieldProps<T>): JSX.Element {
  const { control } = useFormContext<T>();
  const id = React.useId();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <div className={cn('space-y-1.5', className)}>
          {label && (
            <Label htmlFor={id}>
              {label}
              {required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
          )}
          {children({
            value: field.value,
            onChange: field.onChange,
            onBlur: field.onBlur,
            id,
            invalid: !!fieldState.error,
          })}
          {fieldState.error ? (
            <p className="text-xs text-destructive">{fieldState.error.message}</p>
          ) : hint ? (
            <p className="text-xs text-muted-foreground">{hint}</p>
          ) : null}
        </div>
      )}
    />
  );
}
